import type { Order, PaymentMethod } from "../../api/types";
import { t } from "../../i18n";
import { formatTime } from "../../lib/time";
import { discountLabel } from "../general/general";

/** "tafel 4", "levering", "afhaal", as in the prototype's list. */
export function kindOf(order: Order): string {
  return order.channel === "table" && order.table !== null
    ? t.orders.table(order.table)
    : t.sale.channels[order.channel].toLowerCase();
}

/** "2× Durum Mix, Coca-Cola 33 cl" */
export function contentOf(order: Order): string {
  return order.lines
    .map((line) => {
      const name = line.variant === null ? line.name : `${line.name} ${line.variant}`;
      return line.quantity > 1 ? `${String(line.quantity)}× ${name}` : name;
    })
    .join(", ");
}

/** "Kerkstraat 3 bus 2" (prototype: street, nr, "bus" and the box). */
export function streetLine(customer: Record<string, string>): string {
  const { street = "", number = "", box = "" } = customer;
  return `${street} ${number}${box ? ` bus ${box}` : ""}`.trim();
}

/** "Kerkstraat 3 bus 2, Tielrode" for deliveries (prototype custAddr), otherwise the customer's name. */
export function addressOf(order: Order): string {
  const { zone, name } = order.customer;
  return order.channel === "delivery" ? [streetLine(order.customer), zone].filter(Boolean).join(", ") : (name ?? "");
}

/** "Contant", "Bancontact + Contant"; nothing yet: "—". */
export function paymentOf(order: Order): string {
  const methods = [
    ...new Set(order.payments.filter((payment) => payment.kind === "payment").map((payment) => payment.method)),
  ];
  // nothing paid yet, but its receipt asks for it: the QR that Bancontact confirms by itself (ADR 0029)
  if (methods.length === 0 && order.receiptQr !== null) return t.checkout.receiptQr.waiting;
  return methods.length === 0 ? "—" : methods.map((method) => t.paymentMethods[method]).join(" + ");
}

/** A delivery that still has to be delivered: not cancelled and not handed over, also when paid beforehand. */
export function toDeliver(order: Order): boolean {
  return order.channel === "delivery" && order.status !== "cancelled" && order.deliveredAt === null;
}

/**
 * The status as the prototype writes it: "geannuleerd · door Kaan", "te leveren", "bij Mehmet",
 * "onderweg · Mehmet", "betaald".
 */
export function StatusPill({ order }: { order: Order }) {
  if (order.status === "cancelled") {
    return (
      <>
        <span className="pill bad">{t.orders.statuses.cancelled}</span>
        {order.cancelledBy !== null && <small className="proto">{t.orders.byWhom(order.cancelledBy)}</small>}
      </>
    );
  }
  if (toDeliver(order)) {
    const courier = order.courier?.name;
    return (
      <span className="pill warn">
        {courier === undefined
          ? t.orders.toDeliver
          : order.departedAt !== null
            ? t.orders.onTheWay(courier)
            : t.orders.withCourier(courier)}
      </span>
    );
  }
  if (order.status === "paid") return <span className="pill ok">{t.orders.statuses.paid}</span>;
  return <span className="pill warn">{t.orders.statuses.open}</span>;
}

/** Each edit under the content (prototype: "✎ 18:14 Kaan + 1× Cola − 1× Ayran"). */
export function EditNotes({ order }: { order: Order }) {
  return order.edits.map((edit, index) => (
    <small key={index} className="editnote">
      <span className="pill warn">{t.orders.editPill(formatTime(edit.at), edit.by)}</span>{" "}
      <span className="proto">
        {[
          edit.added.length > 0 ? `+ ${edit.added.join(", ")}` : "",
          edit.removed.length > 0 ? `− ${edit.removed.join(", ")}` : "",
          edit.discount === undefined
            ? ""
            : t.orders.editDiscount(edit.discount === null ? null : discountLabel(edit.discount)),
        ]
          .filter(Boolean)
          .join(" ")}
      </span>
    </small>
  ));
}

/** Under the payment (prototype): "⚠ gewijzigd · was Contant · gewijzigd door Elif 18:14". */
export function MethodChangeNote({ order }: { order: Order }) {
  const change = order.methodChange;
  if (change === null) return null;
  return (
    <>
      <span className="pill warn">{t.orders.methodChangedPill}</span>
      <small className="proto">
        {t.orders.methodChangedNote(t.paymentMethods[change.was], change.by, formatTime(change.at))}
      </small>
    </>
  );
}

/** A delivery that can go on a route: still to be delivered, with a street (prototype canRoute). */
export function canRoute(order: Order): boolean {
  return toDeliver(order) && Boolean(order.customer.street);
}

/**
 * An order of Takeaway or Uber Eats whose payment the owner may set right (Antalya, 17/10/2026: the bridge read
 * paid or not paid wrong). A Mymenu.be order's payment comes from Mymenu.be itself and stays Tafo's.
 */
export function correctablePlatform(order: Order): boolean {
  return order.source === "online" && (order.platform === "takeaway" || order.platform === "ubereats");
}

/** In the order of the prototype (PAYMETHODS); online payments are never changed at the till. */
export const METHODS: PaymentMethod[] = ["cash", "bancontact", "bank_qr", "card", "meal_voucher"];

/** What a platform order can be paid with: online first, then what is taken at the door or the counter. */
export const PLATFORM_METHODS: PaymentMethod[] = ["online", "cash", "bancontact", "bank_qr", "card"];

/** The choices of "Betaalwijze wijzigen" for this order. */
export function methodsFor(order: Order): PaymentMethod[] {
  return correctablePlatform(order) ? PLATFORM_METHODS : METHODS;
}

/**
 * Which actions the order offers. Everyone sees them; the code window then asks the code of
 * someone with the right (as in the prototype), and the server checks every action again.
 */
export function actionsFor(order: Order) {
  const own = order.source === "till"; // online orders: only Tafo
  // Takeaway and Uber Eats: how it was paid, set right by the owner (Antalya, 17/10/2026)
  const platform = correctablePlatform(order);
  // an order in a closed Z can still change: it then counts in the next Z of that day (Furkan, 11/10/2026:
  // "z rapport bi engel olusturmamali")
  const active = order.status !== "cancelled";
  const payments = order.payments.filter((payment) => payment.kind === "payment");
  // another online order (Mymenu.be) paid at the counter: a takeaway comes in paid in cash, paid with Bancontact
  // after all the till says so (Furkan, 23/10/2026); what was paid online never changes here
  const atCounter =
    order.source === "online" &&
    !platform &&
    payments.length > 0 &&
    payments.every((payment) => payment.method !== "online");
  return {
    edit: own && active,
    changeMethod: (own || platform || atCounter) && active && payments.length > 0,
    // "⚠ Betaling nakijken" and paid on the platform after all: the courier collects nothing
    paidOnline: platform && active && order.status === "open",
    // deliveries are paid at the door, never at the till
    pay: active && order.status === "open" && order.channel !== "delivery",
    // electronic payments cannot be reversed from the till, so those orders stay as they are; Takeaway and Uber
    // Eats when the platform cancelled them, their online money is the platform's (Antalya, 19/10/2026)
    cancel:
      (own || platform) &&
      active &&
      payments.every((payment) => payment.method === "cash" || (platform && payment.method === "online")),
  };
}

/**
 * Deleting (owner, behind the small ⚙ in Beheer › Bestellingen): only the shop's own orders that are
 * unpaid or paid in cash, also after they were cancelled. An order in a closed Z too (Furkan,
 * 05/10/2026), but then with a reason: the Z is recomputed and the removal is kept. The server refuses
 * anything else too.
 */
export function canDelete(order: Order): boolean {
  return order.source === "till" && order.payments.every((payment) => payment.method === "cash");
}

/** Editing an order that was (partly) paid needs a code; an unpaid one does not. */
export function editNeedsCode(order: Order): boolean {
  return order.paidCents > 0;
}

/**
 * Whether the customer gets mails about this order (prototype validMail): a takeaway or delivery with
 * an e-mail address. The server decides; this is what the cancel window says beforehand.
 */
export function mailsCustomer(order: Order): boolean {
  return order.channel !== "table" && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(order.customer.email ?? "");
}

/**
 * The filters above Bestellingen (Furkan, 11/10/2026: "ordenen op soort afhaal, tafel, levering, online,
 * op betaalwijze cash, online, bancontact, bancontact pro"). An online order is "online", whatever its
 * channel; afhaal and levering are the ones taken at the till.
 */
export const KIND_FILTERS = ["all", "takeaway", "table", "delivery", "online"] as const;
export type KindFilter = (typeof KIND_FILTERS)[number];
export const METHOD_FILTERS = ["all", "cash", "online", "bancontact", "bank_qr"] as const;
export type MethodFilter = (typeof METHOD_FILTERS)[number];

export function matchesKind(order: Order, kind: KindFilter): boolean {
  if (kind === "all") return true;
  if (kind === "online") return order.source === "online";
  return order.source !== "online" && order.channel === kind;
}

/** Paid (also partly) with this method, after another method was chosen or money handed back. */
export function matchesMethod(order: Order, method: MethodFilter): boolean {
  if (method === "all") return true;
  const net = order.payments
    .filter((payment) => payment.method === method)
    .reduce((total, payment) => total + payment.amountCents, 0);
  return net > 0;
}
